/*
 * When a recurring club event actually happens.
 *
 * A repeating event is one record with a rule on it — repeat, repeatUntil
 * and a list of dates it skips — so this is the one place that turns that
 * rule into dates. Everything that draws an event (the calendar, the day
 * list, the club screen) goes through here, so a Friday night is on the
 * same Fridays wherever it is read.
 *
 * The server keeps its own copy of this in server/utils/eventRecurrence.js:
 * it has to answer "has this been and gone" without shipping the whole
 * expansion to a browser. The two must agree — change one, change both.
 */

/** A year out. A Friday night nobody ever ends should not fill the calendar
    to the end of time, and a club that means it can say so with an end date. */
const DEFAULT_HORIZON_DAYS = 366;

const dayOnly = v => (v ? String(v).slice(0, 10) : '');

const toUTC = isoDate => {
  const [y, m, d] = String(isoDate).split('-').map(Number);
  return (y && m && d) ? Date.UTC(y, m - 1, d) : NaN;
};

const fromUTC = ms => new Date(ms).toISOString().slice(0, 10);

const addDays = (isoDate, n) => fromUTC(toUTC(isoDate) + n * 86400000);

/* Months are not a fixed number of days, so this steps the month field and
   clamps: a series starting on the 31st lands on the 30th of a short month
   rather than skidding into the next one. */
const addMonths = (isoDate, n) => {
  const [y, m, d] = String(isoDate).split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + n, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return fromUTC(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, lastDay)));
};

const weekdayOf = isoDate => new Date(toUTC(isoDate)).getUTCDay();

/**
 * The nth given weekday of a month — the second Monday, the last Saturday.
 *
 * `which` is 1 to 4, or 'last'. Not 5: a fifth Monday exists in some months
 * and not others, and a rule that silently skips March is not a rule a club
 * can put on a noticeboard. A club that means the final one means 'last',
 * which every month has.
 */
function nthWeekdayOf(year, monthIndex, weekday, which) {
  if (which === 'last') {
    const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0));
    const back = (lastDay.getUTCDay() - weekday + 7) % 7;
    return fromUTC(lastDay.getTime() - back * 86400000);
  }
  const first = new Date(Date.UTC(year, monthIndex, 1));
  const forward = (weekday - first.getUTCDay() + 7) % 7;
  const day = 1 + forward + (Math.max(1, Math.min(4, Number(which) || 1)) - 1) * 7;
  return fromUTC(Date.UTC(year, monthIndex, day));
}

/* Which week of the month the start date falls in, so a club that picks the
   second Monday and never touches the week box gets the second Monday. */
const weekOfMonth = isoDate => String(Math.floor((Number(String(isoDate).slice(8, 10)) - 1) / 7) + 1);

/* Counted from the start date, not from the last occurrence: stepping a
   month at a time off a 31st walks the series backwards — 31 Jan, 28 Feb,
   28 Mar — where a club means the 31st of every month it has one. */
const STEPS = {
  weekly: (start, i) => addDays(start, 7 * i),
  fortnightly: (start, i) => addDays(start, 14 * i),
  monthly: (start, i) => addMonths(start, i),
  /* By the day of the week rather than the date: a club's committee meets on
     the second Monday, which is a different number every month. */
  'monthly-nth': (start, i, ev) => {
    const [y, m] = start.split('-').map(Number);
    const target = new Date(Date.UTC(y, m - 1 + i, 1));
    return nthWeekdayOf(
      target.getUTCFullYear(), target.getUTCMonth(),
      weekdayOf(start), (ev && ev.repeatWeek) || weekOfMonth(start),
    );
  },
};

/** The last day a series can run: its own end date, or a year from its start. */
function seriesEnd(ev) {
  const start = dayOnly(ev && ev.startDate);
  if (!start) return '';
  const until = dayOnly(ev && ev.repeatUntil);
  return until || addDays(start, DEFAULT_HORIZON_DAYS);
}

const skipSet = ev => new Set(
  (Array.isArray(ev && ev.repeatSkips) ? ev.repeatSkips : []).map(dayOnly).filter(Boolean),
);

/**
 * Every day this event starts on, within [from, to] inclusive.
 *
 * A one-off is its own single occurrence — callers can treat repeating and
 * one-off events the same way rather than branching on repeat everywhere.
 * A multi-day event is still one occurrence here; spreading it across the
 * days it covers is the caller's job, and unchanged by repeating.
 */
function occurrencesBetween(ev, from, to) {
  const start = dayOnly(ev && ev.startDate);
  if (!start) return [];

  const lo = dayOnly(from) || start;
  const hi = dayOnly(to) || seriesEnd(ev);
  const step = STEPS[ev && ev.repeat];
  if (!step) return (start >= lo && start <= hi) ? [start] : [];

  const stop = [seriesEnd(ev), hi].sort()[0];
  const skips = skipSet(ev);
  const out = [];
  /* A guard, not a limit anyone should reach: weekly for a year is 53. It
     stops a rule that somehow fails to advance from hanging the page. */
  for (let i = 0; i < 800; i += 1) {
    const day = step(start, i, ev);
    if (!day || day > stop) break;
    /* Never before the day the series starts. "The second Monday" in the
       month it was set up can fall earlier in that month than the date it
       was set up on, and a series does not run backwards from its own
       start. */
    if (day >= start && day >= lo && !skips.has(day)) out.push(day);
  }
  return out;
}

/** The next day this event is on, today included. Null once it is over. */
function nextOccurrence(ev, today) {
  const from = dayOnly(today) || new Date().toISOString().slice(0, 10);
  return occurrencesBetween(ev, from, seriesEnd(ev))[0] || null;
}

const REPEAT_LABELS = {
  weekly: 'Every week',
  fortnightly: 'Every second week',
  monthly: 'Every month, on the date',
  'monthly-nth': 'Every month, on the weekday',
};

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_WORDS = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', last: 'last' };

const WEEK_OPTIONS = [
  ['1', 'First'],
  ['2', 'Second'],
  ['3', 'Third'],
  ['4', 'Fourth'],
  ['last', 'Last'],
];

/** "The second Monday of every month" — the rule said the way a club says it. */
function ordinalWeekdayPhrase(ev) {
  const start = dayOnly(ev && ev.startDate);
  if (!start) return '';
  const which = WEEK_WORDS[(ev && ev.repeatWeek) || weekOfMonth(start)] || 'first';
  return `The ${which} ${WEEKDAY_NAMES[weekdayOf(start)]} of every month`;
}

/** "Every week until 18 Dec" — what the rule means, in a row of a list. */
function repeatLabel(ev) {
  const base = ev && ev.repeat === 'monthly-nth'
    ? ordinalWeekdayPhrase(ev)
    : REPEAT_LABELS[ev && ev.repeat];
  if (!base) return '';
  const until = dayOnly(ev && ev.repeatUntil);
  if (!until) return base;
  const d = new Date(`${until}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? base
    : `${base} until ${d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}`;
}

export {
  occurrencesBetween, nextOccurrence, seriesEnd, repeatLabel, ordinalWeekdayPhrase,
  REPEAT_LABELS, WEEK_OPTIONS,
};
